import { Box, useTheme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { FC } from 'react';
import { AccountingType } from 'v2-types/order';

import { zodResolver } from '@hookform/resolvers/zod';
import { DeleteConfirmationModal } from 'components/delete-confirmation';
import AddEntry from '../add-entry-modal/add-entry-modal';
import { createAddEntrySchema } from '../add-entry-modal/services/validate-entry';
import TitleSection from '../mortgage-detail/components/title-section';
import { TextButton } from 'components/TextButton';
import LineItem from './components/single-line-item';
import useMortgageItems from './line-items-hook';
import useLineItemVariant from './use-line-item-variant';
import { useTranslation } from 'react-i18next';

const addEntrySchema = createAddEntrySchema();

const useStyles = makeStyles((theme: any) => ({
  list: {
    display: 'flex',
    flexDirection: 'column',
  },
  addButton: {
    '@media (hover: none) and (pointer: coarse)': {
      '& .MuiButton-root': { minHeight: theme.spacing(5.5) },
    },
  },
}));

type LineItemsProps = {
  accounting: AccountingType[];
  mortgateId: string;
};

const LineItems: FC<LineItemsProps> = ({ accounting, mortgateId }) => {
  const { state, dispatch, deleteEntry, isDeleting, id } = useMortgageItems(mortgateId);
  const { t } = useTranslation();
  const theme = useTheme();
  const classes = useStyles();
  const variant = useLineItemVariant();

  const addButton = (
    <Box className={classes.addButton}>
      <TextButton
        icon="plus"
        text={t('payoffs:add-new-line-item')}
        textColor={theme.palette.secondary.main}
        onClick={() => {
          dispatch({ type: 'new' });
        }}
      />
    </Box>
  );

  const totalDue = accounting.find((element) => element.code.includes('total_due'));

  return (
    <TitleSection title={t('payoffs:line-items')} actionButton={addButton}>
      <Box className={classes.list}>
        {accounting
          .filter((entry) => entry.code.includes('mortgage'))
          .map((entry) => (
            <LineItem
              key={entry._id}
              variant={variant}
              value={entry.amount}
              label={t(`payoffs:${entry.code.split('_')[1] || ''}`)}
              date={entry?.entry_date || ''}
              handleEdit={() => {
                dispatch({
                  type: 'edit',
                  entryLedger: entry,
                });
              }}
            />
          ))}

        {accounting
          .filter((entry) => entry.code.includes('line_item'))
          .map((entry) => (
            <LineItem
              key={entry._id}
              variant={variant}
              value={entry.amount}
              label={t(`payoffs:${entry.description}`)}
              date={entry?.entry_date || ''}
              handleEdit={() => {
                dispatch({
                  type: 'edit',
                  entryLedger: entry,
                });
              }}
              handleDelete={() => {
                dispatch({
                  type: 'delete',
                  entryLedger: entry,
                });
              }}
            />
          ))}
        {totalDue && (
          <LineItem
            key={totalDue._id}
            variant={variant}
            value={totalDue.amount}
            label={t('payoffs:total-due')}
            date=""
            hideActions
            hideDate
            isTotal
          />
        )}

        {(state.mode === 'edit' || state.mode === 'new') && (
          <AddEntry
            open={state.mode === 'edit' || state.mode === 'new'}
            handleClose={() => {
              dispatch({ type: null, entryLedger: null });
            }}
            prevData={state.entryLedger}
            kindAllowedEntry={['manual']}
            codeAllowedEntry={['line_item']}
            resolver={zodResolver(addEntrySchema)}
            hideFields={{
              code: true,
              kind: true,
            }}
            mortgageId={mortgateId}
            additionalamount={
              accounting.find((acc) => acc.code.includes('principal'))?.amount_override || 0
            }
          />
        )}

        <DeleteConfirmationModal
          isOpen={state.mode === 'delete'}
          title={t('payoffs:delete-line-item')}
          confirmationMessage={t('payoffs:delete-line-item-confirmation', {
            name: state.entryLedger ? t(`payoffs:${state.entryLedger.description}`) : '',
          })}
          onClose={() => {
            dispatch({ type: null, entryLedger: null });
          }}
          onConfirm={() => {
            if (state.entryLedger?._id) {
              deleteEntry({ id, accountingId: state.entryLedger._id });
            }
          }}
          isRemoveLoading={isDeleting}
          buttonConfirmText={t('common:delete')}
        />
      </Box>
    </TitleSection>
  );
};

export default LineItems;
